/**
 * useColumnReorder — Column order management via drag-and-drop.
 *
 * Uses @dnd-kit/core PointerSensor for pointer drag.
 * Keyboard reorder (Ctrl+Left/Right) is triggered by ResizableHeader consumers
 * and propagates through the same onReorder path as drag.
 * Persists column order to localStorage when storageKey provided.
 * Syncs with external column additions/removals via columnIdsKey comparison.
 */

import type {
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useCallback, useEffect, useRef, useState } from 'react';

function resolveOrder(
  columnIds: string[],
  storageKey: string | undefined,
  controlled: boolean
): string[] {
  if (controlled) {
    return columnIds;
  }
  if (storageKey) {
    return loadOrder(columnIds, storageKey);
  }
  return columnIds;
}

function computeTargetIndex(
  oldIdx: number,
  overIdx: number,
  droppingAfter: boolean
): number {
  // Dropping after: place at overIdx (or overIdx + 1 if coming from left)
  // When moving right (oldIdx < overIdx), arrayMove shifts items left, so overIdx is correct
  // When moving left (oldIdx > overIdx), we want to be AFTER overIdx, so use overIdx + 1
  if (droppingAfter) {
    return oldIdx > overIdx ? overIdx + 1 : overIdx;
  }
  // Dropping before: place at overIdx (or overIdx - 1 if coming from right)
  // When moving left (oldIdx > overIdx), arrayMove shifts items right, so overIdx is correct
  // When moving right (oldIdx < overIdx), we want to be BEFORE overIdx, so use overIdx - 1
  return oldIdx < overIdx ? overIdx - 1 : overIdx;
}

function loadOrder(columnIds: string[], storageKey: string): string[] {
  try {
    const stored = localStorage.getItem(`${storageKey}_order`);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const storedIds = parsed as string[];
        const currentSet = new Set(columnIds);
        // Keep stored order for existing columns, append new ones at end
        const filtered = storedIds.filter(id => currentSet.has(id));
        const filteredSet = new Set(filtered);
        const newCols = columnIds.filter(id => !filteredSet.has(id));
        return [...filtered, ...newCols];
      }
    }
  } catch {
    // localStorage unavailable or invalid JSON — ignore
  }
  return columnIds;
}

export interface UseColumnReorderOptions {
  /** When true, incoming `columnIds` order is authoritative: internal order adopts
   *  `columnIds` on change and localStorage is neither seeded nor written. */
  controlled?: boolean;
  /** Called with the full new right-pane order whenever a DRAG or keyboard reorder
   *  changes order. Never called on drop-in-place, nor from adoption/sync effects. */
  onReorder?: (order: string[]) => void;
}

export interface UseColumnReorderResult {
  /** Current column order as an array of column IDs */
  columnOrder: string[];
  /** @dnd-kit sensors to pass to DndContext */
  sensors: ReturnType<typeof useSensors>;
  /** @dnd-kit collision detection to pass to DndContext */
  collisionDetection: typeof closestCenter;
  /** Pass to DndContext onDragStart */
  handleDragStart: (event: DragStartEvent) => void;
  /** Pass to DndContext onDragOver */
  handleDragOver: (event: DragOverEvent) => void;
  /** Pass to DndContext onDragMove */
  handleDragMove: (event: DragMoveEvent) => void;
  /** Pass to DndContext onDragEnd */
  handleDragEnd: (event: DragEndEvent) => void;
  /** Move a column left or right by one position (for keyboard accessibility) */
  reorderByKeyboard: (colId: string, direction: 'left' | 'right') => void;
  /** Currently dragging column ID (null when not dragging) */
  activeId: string | null;
  /** Column ID currently being hovered over (null when not over any column) */
  overId: string | null;
  /** Horizontal drag offset from start position (positive = moving right) */
  dragDeltaX: number;
}

/**
 * Manages column order via drag-and-drop with localStorage persistence.
 *
 * @param columnIds - Ordered list of column IDs (initial/reset state)
 * @param storageKey - Optional localStorage key prefix for persistence
 */
export function useColumnReorder(
  columnIds: string[],
  storageKey?: string,
  options?: UseColumnReorderOptions
): UseColumnReorderResult {
  const controlled = options?.controlled ?? false;

  // Stable JSON representation used as effect dependency to avoid reference issues
  const columnIdsKey = JSON.stringify(columnIds);

  const onReorderRef = useRef(options?.onReorder);
  useEffect(() => {
    onReorderRef.current = options?.onReorder;
  }, [options?.onReorder]);

  // Mirrors onReorderRef: saveOrder is called from handleDragEnd/reorderByKeyboard,
  // which can be invoked after `controlled` has changed mid-drag. Reading a ref
  // instead of the closed-over `controlled` value avoids writing to localStorage
  // (or skipping the write) based on a stale mode.
  const controlledRef = useRef(controlled);
  useEffect(() => {
    controlledRef.current = controlled;
  }, [controlled]);

  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    resolveOrder(columnIds, storageKey, controlled)
  );
  // Resync order when storageKey changes (e.g. switching entity type). In
  // controlled mode the incoming `columnIds` stays authoritative — resolveOrder
  // ignores localStorage — so a storageKey change never surfaces a stale
  // per-device order over the controlled source.
  const prevStorageKeyRef = useRef(storageKey);
  useEffect(() => {
    if (prevStorageKeyRef.current !== storageKey) {
      prevStorageKeyRef.current = storageKey;
      const currentIds: string[] = JSON.parse(columnIdsKey);
      setColumnOrder(resolveOrder(currentIds, storageKey, controlled));
    }
  }, [storageKey, columnIdsKey, controlled]);

  // Resync when switching between controlled and uncontrolled mode
  const prevControlledRef = useRef(controlled);
  useEffect(() => {
    if (prevControlledRef.current !== controlled) {
      prevControlledRef.current = controlled;
      const currentIds: string[] = JSON.parse(columnIdsKey);
      setColumnOrder(resolveOrder(currentIds, storageKey, controlled));
    }
  }, [controlled, columnIdsKey, storageKey]);

  // Sync when columns are added or removed from the prop (or, in controlled mode,
  // adopt the incoming order verbatim)
  useEffect(() => {
    const currentIds: string[] = JSON.parse(columnIdsKey);
    setColumnOrder(prev => {
      if (controlled) {
        if (
          prev.length === currentIds.length &&
          prev.every((id, i) => id === currentIds[i])
        ) {
          return prev; // already equal — no re-render (loop breaker)
        }
        return currentIds; // adopt parent order verbatim
      }
      const currentSet = new Set(currentIds);
      const filtered = prev.filter(id => currentSet.has(id));
      const filteredSet = new Set(filtered);
      const newCols = currentIds.filter(id => !filteredSet.has(id));
      if (newCols.length === 0 && filtered.length === prev.length) {
        return prev; // No change — return same reference to avoid re-render
      }
      return [...filtered, ...newCols];
    });
  }, [columnIdsKey, controlled]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Track active drag state for DragOverlay and drop indicator
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [dragDeltaX, setDragDeltaX] = useState<number>(0);

  const saveOrder = useCallback(
    (order: string[]) => {
      if (!storageKey || controlledRef.current) {
        return;
      }
      try {
        localStorage.setItem(`${storageKey}_order`, JSON.stringify(order));
      } catch {
        // localStorage quota exceeded — ignore
      }
    },
    [storageKey]
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const newOverId = event.over ? String(event.over.id) : null;
    setOverId(newOverId);

    if (!event.over) {
      lastOverIdRef.current = null;
      return;
    }

    // Determine if the dragged element's center is left or right of the hovered element's center
    if (event.over.rect && event.active.rect.current.translated) {
      const overCenterX = event.over.rect.left + event.over.rect.width / 2;
      const activeCenterX =
        event.active.rect.current.translated.left +
        event.active.rect.current.translated.width / 2;

      const diff = activeCenterX - overCenterX;
      // Hysteresis threshold: 25% of column width prevents flicker near center
      const threshold = event.over.rect.width * 0.25;

      // Only update if we've crossed the center by a meaningful amount,
      // OR if we've changed which column we're hovering over (reset hysteresis)
      if (Math.abs(diff) > threshold || newOverId !== lastOverIdRef.current) {
        const delta = diff >= 0 ? 1 : -1;
        dragDeltaXRef.current = delta;
        setDragDeltaX(delta);
      }
      // If within threshold on same column, keep previous value (hysteresis)

      lastOverIdRef.current = newOverId;
    }
  }, []);

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    // DragMove fires frequently during drag - we don't need to update here
    // since handleDragOver already tracks position relative to hovered element
    // Keep this handler for potential future use but make it a no-op
    void event;
  }, []);

  // Store dragDeltaX in a ref so handleDragEnd can access the final value
  const dragDeltaXRef = useRef<number>(0);
  // Track last overId for hysteresis reset when changing columns
  const lastOverIdRef = useRef<string | null>(null);
  useEffect(() => {
    dragDeltaXRef.current = dragDeltaX;
  }, [dragDeltaX]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      // Capture the final dragDeltaX before clearing state
      const finalDragDeltaX = dragDeltaXRef.current;

      // Clear drag state regardless of outcome
      setActiveId(null);
      setOverId(null);
      setDragDeltaX(0);
      lastOverIdRef.current = null;

      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }

      // Compute the new order outside the setColumnOrder updater — required for
      // React StrictMode correctness, since onReorder is not idempotent and must
      // fire exactly once.
      const current = columnOrder;
      const activeIdStr = String(active.id);
      let next: string[] | null = null;

      if (over.id === '__drop-zone-start') {
        // Virtual drop zone at start: move dragged column to first position
        const oldIdx = current.indexOf(activeIdStr);
        if (oldIdx > 0) {
          next = arrayMove(current, oldIdx, 0);
        }
      } else if (over.id === '__drop-zone-end') {
        // Virtual drop zone at end: move dragged column to last position
        const oldIdx = current.indexOf(activeIdStr);
        if (oldIdx !== -1 && oldIdx !== current.length - 1) {
          next = arrayMove(current, oldIdx, current.length - 1);
        }
      } else {
        const oldIdx = current.indexOf(activeIdStr);
        const overIdx = current.indexOf(String(over.id));
        if (oldIdx !== -1 && overIdx !== -1) {
          // Determine actual target position based on drop indicator position:
          // - dragDeltaX >= 0 means "after" the hovered column
          // - dragDeltaX < 0 means "before" the hovered column
          const droppingAfter = finalDragDeltaX >= 0;
          const targetIdx = computeTargetIndex(oldIdx, overIdx, droppingAfter);

          // Skip if no actual position change
          if (targetIdx !== oldIdx) {
            next = arrayMove(current, oldIdx, targetIdx);
          }
        }
      }

      if (next) {
        setColumnOrder(next);
        saveOrder(next); // no-op when controlled
        onReorderRef.current?.(next);
      }
    },
    [columnOrder, saveOrder]
  );

  const reorderByKeyboard = useCallback(
    (colId: string, direction: 'left' | 'right') => {
      // Compute the new order outside the setColumnOrder updater — required for
      // React StrictMode correctness, since onReorder is not idempotent and must
      // fire exactly once (matches handleDragEnd).
      const current = columnOrder;
      const idx = current.indexOf(colId);
      if (idx === -1) {
        return;
      }
      const targetIdx = direction === 'left' ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= current.length) {
        return;
      }
      const next = arrayMove(current, idx, targetIdx);
      setColumnOrder(next);
      saveOrder(next); // no-op when controlled
      onReorderRef.current?.(next); // keep active saved view authoritative
    },
    [columnOrder, saveOrder]
  );

  return {
    columnOrder,
    sensors,
    collisionDetection: closestCenter,
    handleDragStart,
    handleDragOver,
    handleDragMove,
    handleDragEnd,
    reorderByKeyboard,
    activeId,
    overId,
    dragDeltaX,
  };
}
